/**
 * carousel-3d.js
 * Custom element: <carousel-3d>
 *
 * Manages a 3D perspective carousel with auto-play, prev/next navigation,
 * play/pause, loop mode, touch/swipe, and a progress bar overlaid on image.
 */

if (!customElements.get("carousel-3d")) {
  class Carousel3D extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isPlaying = false;
      this.isLooping = true;
      this.autoplayTimer = null;
      this.progressRAF = null;
      this.progressStart = 0;
      this.touchStartX = 0;
      this.touchDelta = 0;
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll(".carousel-3d__slide"));
      this.totalSlides = this.slides.length;

      if (this.totalSlides === 0) return;

      // Controls
      this.btnPrev = this.querySelector('[data-action="prev"]');
      this.btnNext = this.querySelector('[data-action="next"]');
      this.btnPlay = this.querySelector('[data-action="play"]');
      this.btnLoop = this.querySelector('[data-action="loop"]');
      this.btnList = this.querySelector('[data-action="list"]');

      // Progress
      this.progressBar = this.querySelector(".carousel-3d__progress-bar");
      this.progressThumb = this.querySelector(".carousel-3d__progress-thumb");
      this.progressTrack = this.querySelector(".carousel-3d__progress");

      // Settings
      this.autoplaySpeed =
        parseInt(this.getAttribute("autoplay-speed") || "5", 10) * 1000;
      const shouldAutoplay = this.hasAttribute("autoplay");

      this.bindEvents();
      this.updatePositions();

      if (shouldAutoplay) {
        this.play();
      }
    }

    disconnectedCallback() {
      this.pause();
      this.removeEventListeners();
    }

    bindEvents() {
      this._onPrev = () => this.prev();
      this._onNext = () => this.next();
      this._onPlay = () => this.togglePlay();
      this._onLoop = () => this.toggleLoop();
      this._onList = () => this.toggleView();
      this._onSlideClick = (e) => this.onSlideClick(e);
      this._onTouchStart = (e) => this.onTouchStart(e);
      this._onTouchMove = (e) => this.onTouchMove(e);
      this._onTouchEnd = () => this.onTouchEnd();
      this._onKeyDown = (e) => this.onKeyDown(e);
      this._onProgressClick = (e) => this.onProgressClick(e);

      if (this.btnPrev) this.btnPrev.addEventListener("click", this._onPrev);
      if (this.btnNext) this.btnNext.addEventListener("click", this._onNext);
      if (this.btnPlay) this.btnPlay.addEventListener("click", this._onPlay);
      if (this.btnLoop) this.btnLoop.addEventListener("click", this._onLoop);
      if (this.btnList) this.btnList.addEventListener("click", this._onList);

      // Progress bar click to seek
      if (this.progressTrack)
        this.progressTrack.addEventListener("click", this._onProgressClick);

      this.slides.forEach((slide) => {
        slide.addEventListener("click", this._onSlideClick);
      });

      const viewport = this.querySelector(".carousel-3d__viewport");
      if (viewport) {
        viewport.addEventListener("touchstart", this._onTouchStart, {
          passive: true,
        });
        viewport.addEventListener("touchmove", this._onTouchMove, {
          passive: true,
        });
        viewport.addEventListener("touchend", this._onTouchEnd);
      }

      this.addEventListener("keydown", this._onKeyDown);
    }

    removeEventListeners() {
      if (this.btnPrev) this.btnPrev.removeEventListener("click", this._onPrev);
      if (this.btnNext) this.btnNext.removeEventListener("click", this._onNext);
      if (this.btnPlay) this.btnPlay.removeEventListener("click", this._onPlay);
      if (this.btnLoop) this.btnLoop.removeEventListener("click", this._onLoop);
      if (this.btnList) this.btnList.removeEventListener("click", this._onList);
      if (this.progressTrack)
        this.progressTrack.removeEventListener("click", this._onProgressClick);

      this.slides.forEach((slide) => {
        slide.removeEventListener("click", this._onSlideClick);
      });

      const viewport = this.querySelector(".carousel-3d__viewport");
      if (viewport) {
        viewport.removeEventListener("touchstart", this._onTouchStart);
        viewport.removeEventListener("touchmove", this._onTouchMove);
        viewport.removeEventListener("touchend", this._onTouchEnd);
      }

      this.removeEventListener("keydown", this._onKeyDown);
    }

    /* ---- Navigation ---- */
    goTo(index) {
      if (this.isLooping) {
        this.currentIndex =
          ((index % this.totalSlides) + this.totalSlides) % this.totalSlides;
      } else {
        this.currentIndex = Math.max(0, Math.min(index, this.totalSlides - 1));
      }
      this.updatePositions();
      this.resetProgress();
    }

    prev() {
      this.goTo(this.currentIndex - 1);
    }

    next() {
      this.goTo(this.currentIndex + 1);
    }

    /* ---- Position layout ---- */
    getPosition(offset) {
      if (offset === 0) return "center";
      if (offset === -1) return "left";
      if (offset === 1) return "right";
      if (offset === -2) return "far-left";
      if (offset === 2) return "far-right";
      return "hidden";
    }

    updatePositions() {
      this.slides.forEach((slide, i) => {
        let offset = i - this.currentIndex;

        // Wrap-around for looping
        if (this.isLooping && this.totalSlides > 2) {
          const half = Math.floor(this.totalSlides / 2);
          if (offset > half) offset -= this.totalSlides;
          if (offset < -half) offset += this.totalSlides;
        }

        const position = this.getPosition(offset);
        slide.setAttribute("data-position", position);
        slide.setAttribute(
          "aria-hidden",
          position !== "center" ? "true" : "false",
        );
      });

      // Update progress thumb position (based on slide index)
      this.updateProgressPosition();
    }

    updateProgressPosition() {
      if (!this.progressThumb) return;
      const pct =
        this.totalSlides > 1
          ? (this.currentIndex / (this.totalSlides - 1)) * 100
          : 0;
      this.progressThumb.style.left = `calc(${pct}% - 6px)`;
    }

    /* ---- Progress bar click to seek ---- */
    onProgressClick(e) {
      if (!this.progressTrack || this.totalSlides <= 1) return;
      const rect = this.progressTrack.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const pct = clickX / rect.width;
      const targetIndex = Math.round(pct * (this.totalSlides - 1));
      this.goTo(targetIndex);
    }

    /* ---- Auto-play ---- */
    play() {
      if (this.totalSlides <= 1) return;
      this.isPlaying = true;
      this.updatePlayButton();
      this.startProgress();
    }

    pause() {
      this.isPlaying = false;
      clearTimeout(this.autoplayTimer);
      cancelAnimationFrame(this.progressRAF);
      this.updatePlayButton();
    }

    togglePlay() {
      if (this.isPlaying) {
        this.pause();
      } else {
        this.play();
      }
    }

    toggleLoop() {
      this.isLooping = !this.isLooping;
      if (this.btnLoop) {
        this.btnLoop.classList.toggle("active", this.isLooping);
        this.btnLoop.setAttribute("aria-pressed", String(this.isLooping));
      }
      this.updatePositions();
    }

    /* ---- View toggle (3D carousel / 2D flat carousel) ---- */
    toggleView() {
      const current = this.getAttribute('data-view') || 'carousel';
      const next = current === 'carousel' ? 'strip' : 'carousel';
      this.setAttribute('data-view', next);

      if (next === 'strip') {
        // 2D flat mode: keep positions updated, keep autoplay
        this.updatePositions();
        // Continue playing if autoplay was on
        if (!this.isPlaying && this.hasAttribute('autoplay')) {
          this.play();
        }
      } else {
        // Return to 3D mode
        this.updatePositions();
      }

      // Toggle active state on list button
      if (this.btnList) {
        this.btnList.classList.toggle('active', next === 'strip');
      }
    }

    updatePlayButton() {
      if (!this.btnPlay) return;
      const pauseIcon = this.btnPlay.querySelector('[data-icon="pause"]');
      const playIcon = this.btnPlay.querySelector('[data-icon="play"]');
      if (pauseIcon)
        pauseIcon.style.display = this.isPlaying ? "block" : "none";
      if (playIcon) playIcon.style.display = this.isPlaying ? "none" : "block";
    }

    /* ---- Progress bar ---- */
    startProgress() {
      if (!this.isPlaying) return;
      cancelAnimationFrame(this.progressRAF);
      clearTimeout(this.autoplayTimer);

      this.progressStart = performance.now();

      // Calculate the range for the progress bar:
      // It fills between the current slide's position and the next slide's position
      const segmentStart =
        this.totalSlides > 1
          ? (this.currentIndex / (this.totalSlides - 1)) * 100
          : 0;
      const nextIndex = this.isLooping
        ? (this.currentIndex + 1) % this.totalSlides
        : Math.min(this.currentIndex + 1, this.totalSlides - 1);
      const segmentEnd =
        this.totalSlides > 1
          ? (nextIndex / (this.totalSlides - 1)) * 100
          : 0;

      const animate = (now) => {
        if (!this.isPlaying) return;
        const elapsed = now - this.progressStart;
        const fraction = Math.min(elapsed / this.autoplaySpeed, 1);

        if (this.progressBar) {
          // Fill from segment start to interpolated position towards next slide
          const fillPct = segmentStart + (segmentEnd - segmentStart) * fraction;
          this.progressBar.style.width = fillPct + "%";
        }

        if (elapsed >= this.autoplaySpeed) {
          this.next();
          return;
        }

        this.progressRAF = requestAnimationFrame(animate);
      };

      this.progressRAF = requestAnimationFrame(animate);
    }

    resetProgress() {
      if (this.progressBar) {
        // Set the bar to the current slide's position immediately
        const pct =
          this.totalSlides > 1
            ? (this.currentIndex / (this.totalSlides - 1)) * 100
            : 0;
        this.progressBar.style.width = pct + "%";
      }
      if (this.isPlaying) {
        this.startProgress();
      }
    }

    /* ---- Interaction ---- */
    onSlideClick(e) {
      const slide = e.currentTarget;
      const position = slide.getAttribute("data-position");
      const isStripMode = this.getAttribute('data-view') === 'strip';

      if (position === "left" || position === "far-left") {
        this.prev();
      } else if (position === "right" || position === "far-right") {
        this.next();
      } else if (position === "center") {
        // In strip mode, center click opens link (same as 3D)
        const link = slide.querySelector("a");
        if (link && link.href) {
          window.location.href = link.href;
        }
      }
    }

    onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchDelta = 0;
    }

    onTouchMove(e) {
      this.touchDelta = e.touches[0].clientX - this.touchStartX;
    }

    onTouchEnd() {
      const threshold = 50;
      if (this.touchDelta > threshold) {
        this.prev();
      } else if (this.touchDelta < -threshold) {
        this.next();
      }
      this.touchDelta = 0;
    }

    onKeyDown(e) {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        this.prev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        this.next();
      } else if (e.key === " ") {
        e.preventDefault();
        this.togglePlay();
      }
    }
  }

  customElements.define("carousel-3d", Carousel3D);
}
